import { useCallback, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  organizationUserAddRoleFormSchema,
  type OrganizationUserAddRoleFormData,
} from '@/features/organization-users/components/organization-user-permissions.schema'
import { TranslatedFormMessage } from '@/features/organization-users/components/translated-form-message'
import {
  useDeleteUserRole,
  useOrganizationUserRoles,
  useOrganizationUsers,
  useUpdateUserRole,
} from '@/features/organization-users/hooks/use-organization-users-api'
import type { OrganizationUser } from '@/features/organization-users/types/organization-user'

const defaultAddRoleValues: OrganizationUserAddRoleFormData = {
  roleSlug: '',
}

type OrganizationUserPermissionsDialogProps = {
  organizationId: string
  selfUserId: string
  user: OrganizationUser
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function OrganizationUserPermissionsDialog({
  organizationId,
  selfUserId,
  user,
  open,
  onOpenChange,
}: OrganizationUserPermissionsDialogProps) {
  const { t } = useTranslation(['organizationUsers', 'common'])
  const { data: roles } = useOrganizationUserRoles(organizationId)
  const { data: organizationUsers } = useOrganizationUsers(organizationId)
  const { mutateAsync: updateUserRole, isPending: isSavingRole } =
    useUpdateUserRole(organizationId, user.id)
  const { mutateAsync: deleteUserRole, isPending: isDeletingRole } =
    useDeleteUserRole(organizationId, user.id)

  const isPending = isSavingRole || isDeletingRole

  const form = useForm<OrganizationUserAddRoleFormData>({
    resolver: zodResolver(organizationUserAddRoleFormSchema),
    defaultValues: defaultAddRoleValues,
  })

  useEffect(() => {
    if (open) {
      form.reset(defaultAddRoleValues)
    }
  }, [open, user.id, form])

  const onRevokeRole = useCallback(
    async (roleSlug: string | null) => {
      if (!roleSlug) {
        return
      }
      if (user.id === selfUserId) {
        toast.error(t('organizationUsers:permissionsDialog.errors.ownRole'))
        return
      }

      const admins = organizationUsers?.filter((orgUser) =>
        orgUser.permissions.some((permission) => permission.roleSlug === 'admin')
      )
      const isLastAdmin = admins?.length === 1
      if (isLastAdmin && roleSlug === 'admin') {
        toast.error(t('organizationUsers:permissionsDialog.errors.lastAdmin'))
        return
      }

      try {
        await deleteUserRole({ roleSlug })
      } catch {
        toast.error(t('common:errors.generic'))
      }
    },
    [
      deleteUserRole,
      organizationUsers,
      selfUserId,
      t,
      user.id,
    ]
  )

  async function onSubmitAddRole(data: OrganizationUserAddRoleFormData) {
    if (user.id === selfUserId) {
      toast.error(t('organizationUsers:permissionsDialog.errors.ownRole'))
      return
    }
    try {
      await updateUserRole({ roleSlug: data.roleSlug })
      form.reset(defaultAddRoleValues)
    } catch {
      toast.error(t('common:errors.generic'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>
            {t('organizationUsers:permissionsDialog.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t('organizationUsers:permissionsDialog.fields.permissions')}
                </TableHead>
                <TableHead className="w-[140px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {user.permissions.map((permission) => (
                <TableRow key={permission.roleSlug}>
                  <TableCell>
                    {t(`organizationUsers:roles.${permission.roleSlug}`)}
                  </TableCell>
                  <TableCell className="text-end">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={isPending}
                      onClick={() => {
                        void onRevokeRole(permission.roleSlug)
                      }}
                    >
                      {t('organizationUsers:permissionsDialog.actions.revoke')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-sm text-muted-foreground">
            {t('organizationUsers:permissionsDialog.description')}
          </p>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitAddRole)}
              className="grid gap-4"
            >
              <FormField
                control={form.control}
                name="roleSlug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('organizationUsers:permissionsDialog.fields.selectRole')}
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t(
                              'organizationUsers:permissionsDialog.fields.selectRole'
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roles?.map((role) => (
                          <SelectItem key={role} value={role}>
                            {t(`organizationUsers:roles.${role}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <TranslatedFormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => {
                    onOpenChange(false)
                  }}
                >
                  {t('organizationUsers:permissionsDialog.actions.cancel')}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {t('organizationUsers:permissionsDialog.actions.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
